<template>
  <div class="cc-closing">
    <div class="cc-card-header cc-smart-header">
      <div class="cc-smart-left">
        <div class="cc-card-title">
          <i class="fa-duotone fa-calendar-days"></i>
          Upcoming Closings
        </div>
      </div>

      <div class="cc-smart-right">
        <div class="cc-closing-filter">
          <select v-model="range" class="cc-range-select">
            <option value="7">Next 7 days</option>
            <option value="month">This month</option>
          </select>
        </div>
      </div>
    </div>

    <div class="cc-closing-body">
      <div v-if="loading" class="cc-closing-loading">
        <i class="fa-duotone fa-spinner-third fa-spin"></i>
        Loading closings...
      </div>

      <div v-else class="cc-closing-shell">
        <div v-if="!loans.length" class="cc-closing-empty">
          <i class="fa-duotone fa-calendar-xmark"></i>
          <div class="cc-empty-title">No closings coming up</div>
          <div class="cc-empty-sub">{{ emptySubText }}</div>
        </div>

        <div v-else class="cc-closing-list">
          <div
            v-for="x in displayLoans"
            :key="x.id"
            class="cc-closing-card"
            @click.stop="openLoan(x)"
            :title="`Open Loan ${x.f364 || ''}`"
          >
            <div class="cc-closing-card__top">
              <div class="cc-closing-card__titleline">
                <div class="cc-closing-card__num">
                  <i class="fa-duotone fa-file-invoice"></i>
                  {{ x.f364 || "Loan" }}
                </div>

                <div class="cc-closing-card__borrower">
                  {{ x.name || "—" }}
                </div>
                
            <div class="cc-closing-card__address" v-if="x.addressline">
              <i class="fa-duotone fa-location-dot"></i>
              <span>{{ x.addressline }}</span>
            </div>
              </div>

              <div class="cc-closing-card__date" v-if="x.cust17fv">
                <span class="cc-date-icon">
                  <i class="fa-duotone fa-calendar"></i>
                </span>
                {{ formatDate(x.cust17fv) }}
              </div>
            </div>
            <div class="cc-closing-card__meta">
              <span v-if="x.f2" class="cc-chip cc-chip--money">
                <i class="fa-duotone fa-sack-dollar"></i>
                {{ x.f2 }}
              </span>
              <span v-if="x.f3" class="cc-chip cc-chip--rate">
                <i class="fa-duotone fa-percent"></i>
                {{ x.f3 }}
              </span>
          <span v-if="x.f1172" class="cc-chip cc-chip--type">
  <i class="fa-duotone fa-file-signature"></i>
  {{ x.f1172 }}
</span>

<span v-if="x.f19" class="cc-chip cc-chip--purpose">
  <i class="fa-duotone fa-bullseye-arrow"></i>
  {{ x.f19 }}
</span>
   
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from "@/api/api.service.js"
import { formatDate } from "@/helpers/formatters.js"

export default {
  name: "ClosingNext7DaysCard",
  props: {
    azureId: { type: String, required: true },
    accessToken: { type: String, required: true },
    mineOnly: { type: Boolean, default: false },
    orgId: { type: String, default: null }
  },
  data() {
    return {
      loading: false,
      loans: [],
      limit: 20000,
      range: "7"
    }
  },
  computed: {
    emptySubText() {
      if (this.range === "month") return "Nothing scheduled for the rest of this month"
      return "Nothing scheduled within the next 7 days"
    },

    dateRange() {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      let end

      if (this.range === "month") {
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        end.setHours(23, 59, 59, 999)
      } else {
        end = new Date(start)
        end.setDate(end.getDate() + 7)
        end.setHours(23, 59, 59, 999)
      }

      return {
        start,
        end,
        startStr: this.toIsoDate(start),
        endStr: this.toIsoDate(end)
      }
    },

    displayLoans() {
      return this.loans.slice(0, this.limit)
    }
  },
  watch: {
    azureId: "fetch",
    accessToken: "fetch",
    mineOnly: "fetch",
    orgId: "fetch",
    range: "fetch"
  },
  methods: {
    formatDate,

    toIsoDate(d) {
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      return `${yyyy}-${mm}-${dd}`
    },

    async fetch() {
      if (!this.azureId || !this.accessToken) return
      this.loading = true

      try {
        const { startStr, endStr } = this.dateRange

        const res = await apiService.GetSwiftClosingNext7Days(
          this.azureId,
          this.accessToken,
          this.orgId,
          startStr,
          endStr,
          this.mineOnly
        )

        const raw = res?.data?.data || []
        this.loans = raw
          .filter((x) => x?.cust17fv)
          .sort((a, b) => new Date(a.cust17fv) - new Date(b.cust17fv))
      } catch (e) {
        this.loans = []
      }

      this.loading = false
      this.$emit("loaded", { count: this.loans.length, loans: this.loans })
    },

    openLoan(loan) {
      const id = loan?.id || loan?.loanid || loan?.f364
      if (!id) return
      this.$emit("open-loan", id)
    },

    openTopLoan() {
      if (!this.loans.length) return
      this.openLoan(this.loans[0])
    }
  },
  mounted() {
    this.fetch()
  }
}
</script>

<style scoped>
.cc-closing {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.cc-closing-filter {
  display: flex;
  align-items: center;
}

.cc-range-select {
  border: 1px solid var(--border-ltr);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 900;
  background: var(--background);
  color: var(--text-primary-on-background);
  cursor: pointer;
  outline: none;
}

.cc-range-select:focus {
  border-color: rgba(40, 110, 255, 0.35);
  box-shadow: 0 0 0 3px rgba(40, 110, 255, 0.10);
}

.cc-closing-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.cc-closing-loading {
  flex: 1;
  min-height: 0;
  border-radius: 10px;
  border: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.02);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 950;
  font-size: 12px;
  color: var(--text-secondary-on-background);
}

.cc-closing-shell {
  width: 100%;
  height: 100%;
  background: var(--background);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;

}

.cc-closing-empty {
  flex: 1;
  min-height: 0;
  padding: 26px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  color: var(--text-secondary-on-background);
}

.cc-closing-empty i {
  font-size: 22px;
  opacity: 0.85;
}

.cc-empty-title {
  font-weight: 950;
  font-size: 13px;
  color: var(--text-primary-on-background);
  opacity: 0.9;
}

.cc-empty-sub {
  font-weight: 800;
  font-size: 12px;
  color: var(--text-secondary-on-background);
}

.cc-closing-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;

}

.cc-closing-card {
  border: 1px solid var(--border-ltr);
  border-radius: 10px;
  padding: 10px 10px;
  background: rgba(5, 29, 58, 0.02);
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}

.cc-closing-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  border-color: rgba(40, 110, 255, 0.22);
}

.cc-closing-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.cc-closing-card__titleline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.cc-closing-card__num {
  font-weight: 950;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  color: var(--text-primary-on-background);
}

.cc-closing-card__borrower {
  font-weight: 900;
  font-size: 12px;
  color: var(--text-primary-on-background);
  opacity: 0.88;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-closing-card__date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 950;
  font-size: 11px;
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-ltr);
  background: var(--background);
  color: var(--text-primary-on-background);
  opacity: 0.85;
}

.cc-date-icon {
  width: 24px;
  height: 24px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(40, 110, 255, 0.10);
  border: 1px solid rgba(40, 110, 255, 0.18);
  flex-shrink: 0;
}

.cc-date-icon i.fa-duotone {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.30);
}

.cc-closing-card__address {

  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 850;
  font-size: 11px;
  color: var(--text-primary-on-background);
  opacity: 0.72;
  min-width: 0;
}

.cc-closing-card__address i.fa-duotone {
  --fa-primary-color: rgba(10, 170, 120, 0.95);
  --fa-secondary-color: rgba(10, 170, 120, 0.5);
  flex-shrink: 0;
}

.cc-closing-card__address span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-closing-card__meta {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-ltr);
  background: var(--background);
  font-size: 10.5px;
  font-weight: 950;
  color: var(--text-primary-on-background);
}

.cc-closing-card__num i.fa-duotone {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.30);
}

.cc-chip--money i.fa-duotone {
  --fa-primary-color: rgba(10, 170, 120, 0.95);
  --fa-secondary-color: rgba(10, 170, 120, 0.30);
}

.cc-chip--rate i.fa-duotone {
  --fa-primary-color: rgba(140, 90, 255, 0.95);
  --fa-secondary-color: rgba(140, 90, 255, 0.30);
}

.cc-chip--money {
  border-color: rgba(10, 170, 120, 0.25);
  background: rgba(10, 170, 120, 0.06);
}

.cc-chip--rate {
  border-color: rgba(140, 90, 255, 0.25);
  background: rgba(140, 90, 255, 0.06);
}

.cc-smart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding:10px 10px 0px 10px
}

.cc-smart-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.cc-smart-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.cc-range-select {
  height: 32px;
  padding: 0 10px;
  border-radius: 10px;
}
.cc-smart-header .cc-card-title i.fa-duotone {
  position: relative;
  z-index: 2;
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.22);
}
.cc-closing-list::-webkit-scrollbar {
  width: 10px;
}

.cc-closing-list::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.04);
  border-radius: 999px;
}

.cc-closing-list::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
}

.cc-closing-list::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 23, 42, 0.22);
}
.cc-closing-list {
  padding: 12px 14px 12px 10px;
}

.cc-closing-list::-webkit-scrollbar-button {
  display: none;
  height: 0;
  width: 0;
}

.cc-closing-list::before,
.cc-closing-list::after {
  content: "";
  display: block;
  height: 6px;
  flex: 0 0 auto;
}
.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
}

.cc-chip i {
  font-size: 14px;
  line-height: 1;
}
.cc-chip--type {

  border-color: rgba(37, 99, 235, 0.18);

}

.cc-chip--type i.fa-duotone {
  --fa-primary-color: rgba(37, 99, 235, 0.95);
  --fa-secondary-color: rgba(37, 99, 235, 0.35);
  --fa-secondary-opacity: 1;
}

.cc-chip--purpose {

  border-color: rgba(59, 130, 246, 0.16);

}

.cc-chip--purpose i.fa-duotone {
  --fa-primary-color: rgba(59, 130, 246, 0.90);
  --fa-secondary-color: rgba(59, 130, 246, 0.22);
  --fa-secondary-opacity: 1;
}


</style>
